import { defineEnum, type EnumValueOf } from './define.ts'

/**
 * Who a principal is, and what state their account is in.
 *
 * Two separate ideas live here deliberately:
 *
 * - `userRole` is the **linear hierarchy inside one tenant** — employee < admin <
 *   superadmin — carried over unchanged from the current system
 *   (`docs/reference/backend/data-model.md` §3).
 * - `principalKind` is the tier *above* it. `docs/DECISIONS.md` ("Platform-admin identity
 *   — a separate table, a separate token audience, one Worker") puts platform admins in
 *   their own table with no tenant at all, precisely so that `users.tenant_id` can stay
 *   `NOT NULL` and no tenant-scoped query ever has to reason about "unless it is null".
 *   A platform admin therefore has **no `userRole`**; asking for one is a category error.
 */

/** The per-tenant role hierarchy, in ascending order of authority. */
export const userRole = defineEnum('UserRole', ['employee', 'admin', 'superadmin'])
export type UserRole = EnumValueOf<typeof userRole>

/**
 * Account lifecycle. An invited user is `pending` until they set a nickname and password;
 * only `active` may log in; `disabled` is a reversible lock-out that is not a delete.
 */
export const userStatus = defineEnum('UserStatus', ['pending', 'active', 'disabled'])
export type UserStatus = EnumValueOf<typeof userStatus>

/**
 * Which population a principal belongs to. Tenant users live in `users` and always have a
 * tenant; platform admins live in `platform_admins` and never do.
 */
export const principalKind = defineEnum('PrincipalKind', ['tenant_user', 'platform_admin'])
export type PrincipalKind = EnumValueOf<typeof principalKind>
